// src/games/HuntWords/rules.ts
// Regras puras do caça-palavras (contrato P0): 10 pontos por palavra; o fim é
// o total REAL de palavras do jogo (`word[]`). Cache igual ao do app.
import type { ISolution, IWord } from './huntWordInterface';

export const POINTS_PER_WORD = 10;

export interface WordSearchCache {
  found: ISolution[];
  correctCells: number[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

const keyOf = (id: unknown, solution: unknown) => `${Number(id)}|${String(solution ?? '').toLowerCase()}`;
const solutionKey = (s: ISolution) => keyOf(s.id, s.solution);

function sortedUnique(cells: number[]): number[] {
  return Array.from(new Set(cells)).sort((a, b) => a - b);
}

export function buildSolutions(words: IWord[] | IWord | null | undefined, texto: string | null | undefined): ISolution[] {
  const list = Array.isArray(words) ? words : words ? [words] : [];
  const pieces = (texto ?? '').split('*');
  return list.map((word) => {
    const solution = String(word.attributes.solution);
    return {
      x: String(word.attributes.x).split('-'),
      y: String(word.attributes.y).split('-'),
      id: parseInt(word.attributes.id, 10),
      solution,
      wordId: pieces.findIndex((piece) => piece.toLowerCase() === solution.toLowerCase()),
    };
  });
}

export function wordsearchPoints(foundCount: number): number {
  return foundCount * POINTS_PER_WORD;
}

export function remainingSolutions(all: ISolution[], found: ISolution[]): ISolution[] {
  const foundKeys = new Set(found.map(solutionKey));
  return all.filter((s) => !foundKeys.has(solutionKey(s)));
}

export function isWordSearchComplete(all: ISolution[], found: ISolution[]): boolean {
  return all.length > 0 && remainingSolutions(all, found).length === 0;
}

export function addFound(progress: WordSearchCache, solution: ISolution, cells: number[]): WordSearchCache {
  if (progress.found.some((s) => solutionKey(s) === solutionKey(solution))) return progress;
  return {
    found: [...progress.found, solution],
    correctCells: sortedUnique([...progress.correctCells, ...cells]),
  };
}

export function buildWordSearchCache(found: ISolution[], correctCells: number[]): WordSearchCache {
  return { found, correctCells: sortedUnique(correctCells) };
}

/** Cache salvo (portal ou app) → palavras do jogo atual + células válidas. */
export function readWordSearchCache(cache: unknown, all: ISolution[]): WordSearchCache {
  const raw = isRecord(cache) ? cache : {};
  const byKey = new Map(all.map((s) => [solutionKey(s), s] as const));
  const found: ISolution[] = [];
  const seen = new Set<string>();
  (Array.isArray(raw.found) ? raw.found : []).forEach((entry) => {
    if (!isRecord(entry)) return;
    const key = keyOf(entry.id, entry.solution);
    const match = byKey.get(key);
    if (match && !seen.has(key)) {
      seen.add(key);
      found.push(match);
    }
  });
  const cells = (Array.isArray(raw.correctCells) ? raw.correctCells : []).filter(
    (n): n is number => Number.isInteger(n) && n >= 0
  );
  return { found, correctCells: sortedUnique(cells) };
}
